import { useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";
import { getCurrentUser } from "@/lib/auth";
import type { User } from "@/lib/auth";
import Navbar from "@/components/shared/layout/navbar";
import DashboardContainer from "@/components/shared/layout/DashboardContainer";
import PageHeader from "@/components/shared/layout/PageHeader";
import ProjectGrid from "@/components/shared/dashboard/ProjectGrid";
import ProjectCard from "@/components/shared/dashboard/ProjectCard";
import ProjectCardSkeleton from "@/components/shared/dashboard/ProjectCardSkeleton";
import ToolGrid from "@/components/shared/dashboard/ToolGrid";
import ToolCard from "@/components/shared/dashboard/ToolCard";
import ToolModal from "@/components/shared/ToolModal";
import EmptyState from "@/components/shared/ui/EmptyState";
import Button from "@/components/shared/ui/Button";
import FamilyClubStrategyContentsCard from "@/components/shared/dashboard/FamilyClubStrategyContentsCard";
import HealthTechStrategyContentsCard from "@/components/shared/dashboard/HealthTechStrategyContentsCard";
import DashboardFooterBanner from "@/components/shared/dashboard/DashboardFooterBanner";
import AssetLiteStrategyContentsCard from "@/components/shared/dashboard/AssetLiteStrategyContentsCard";

interface Project {
  id: string;
  name: string;
  tagline?: string;
  thumbnail?: string;
  stage?: string;
  status?: string;
  funding_target?: string;
  current_raise?: string;
  access_level: "visible" | "locked" | "hidden";
  resource_count?: number;
  display_order?: number;
  // Visibility flags from user_project_access
  show_funding?: boolean;
  show_metrics?: boolean;
  show_status?: boolean;
  show_action_buttons?: boolean;
}

interface ProjectThread {
  id: string;
  name: string;
  description?: string;
  display_order: number;
}

interface ThreadWithProjects {
  thread: ProjectThread;
  projects: Project[];
}

interface Tool {
  id: string;
  name: string;
  client?: string;
  purpose?: string;
  features?: string[];
  technical_summary?: string;
  link?: string;
  thumbnail?: string;
  additional_detail?: string;
  access_level: "visible" | "locked" | "hidden";
}

export default function InvestorDashboard() {
  const [user, setUser] = useState<User | null>(null);

  const [accessLevel, setAccessLevel] = useState<
    "investor" | "advisor" | "partner" | "board_member" | null
  >(null);
  const [threadedProjects, setThreadedProjects] = useState<
    ThreadWithProjects[]
  >([]);
  const [tools, setTools] = useState<Tool[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Modal state for tools
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedTool, setSelectedTool] = useState<Tool | null>(null);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);

      // Get current user
      const currentUser = await getCurrentUser();
      if (!currentUser) {
        setError("User not found");
        setLoading(false);
        return;
      }
      setUser(currentUser);

      // Fetch user type AND access level from users table
      const { data: userData, error: userError } = await supabase
        .from("users")
        .select("user_type, access_level")
        .eq("id", currentUser.id)
        .single();

      if (userError) throw userError;

      setAccessLevel(
        userData?.access_level as
          | "investor"
          | "advisor"
          | "partner"
          | "board_member"
          | null
      );

      // Fetch projects for this investor with thread information AND visibility flags
      const { data: projectsData, error: projectsError } = await supabase
        .from("user_project_access")
        .select(
          `
          access_level,
          show_funding,
          show_metrics,
          show_status,
          show_action_buttons,
          projects (
            id,
            name,
            tagline,
            thumbnail,
            stage,
            status,
            funding_target,
            current_raise,
            thread_id,
            display_order,
            project_threads (
              id,
              name,
              description,
              display_order
            )
          )
        `
        )
        .eq("user_id", currentUser.id)
        .neq("access_level", "hidden");

      if (projectsError) throw projectsError;

      // Transform projects data and count resources for each
      const transformedProjects = (await Promise.all(
        projectsData
          .filter((item) => item.projects)
          .map(async (item) => {
            const project = item.projects as any;

            // Count total resources (documents + research + press + competitors)
            const [docsCount, researchCount, pressCount, competitorsCount] =
              await Promise.all([
                supabase
                  .from("documents")
                  .select("id", { count: "exact", head: true })
                  .eq("project_id", project.id),
                supabase
                  .from("project_research")
                  .select("research_item_id", { count: "exact", head: true })
                  .eq("project_id", project.id),
                supabase
                  .from("press_highlights")
                  .select("id", { count: "exact", head: true })
                  .eq("project_id", project.id),
                supabase
                  .from("project_competitors")
                  .select("id", { count: "exact", head: true })
                  .eq("project_id", project.id),
              ]);

            const totalResources =
              (docsCount.count || 0) +
              (researchCount.count || 0) +
              (pressCount.count || 0) +
              (competitorsCount.count || 0);

            return {
              id: project.id,
              name: project.name,
              tagline: project.tagline ?? undefined,
              thumbnail: project.thumbnail ?? undefined,
              stage: project.stage ?? undefined,
              status: project.status ?? undefined,
              funding_target: project.funding_target ?? undefined,
              current_raise: project.current_raise ?? undefined,
              access_level: item.access_level,
              resource_count: totalResources,
              display_order: project.display_order ?? 0,
              thread: project.project_threads,
              // Pass visibility flags from user_project_access
              show_funding: item.show_funding ?? true,
              show_metrics: item.show_metrics ?? true,
              show_status: item.show_status ?? true,
              show_action_buttons: item.show_action_buttons ?? true,
            };
          })
      )) as (Project & { thread: any })[];

      // Group projects by thread
      const projectsByThread = new Map<string, ThreadWithProjects>();

      transformedProjects.forEach((project) => {
        const threadId = project.thread?.id || "no-thread";

        if (!projectsByThread.has(threadId)) {
          projectsByThread.set(threadId, {
            thread: project.thread || {
              id: "no-thread",
              name: "General & Live",
              description: "Active investment opportunities",
              display_order: 0,
            },
            projects: [],
          });
        }

        const { thread, ...projectWithoutThread } = project;
        projectsByThread.get(threadId)!.projects.push(projectWithoutThread);
      });

      // Sort threads by display_order
      const sortedThreads = Array.from(projectsByThread.values()).sort(
        (a, b) => a.thread.display_order - b.thread.display_order
      );

      // Sort projects within each thread by display_order
      sortedThreads.forEach((threadData) => {
        threadData.projects.sort(
          (a, b) => (a.display_order ?? 0) - (b.display_order ?? 0)
        );
      });

      setThreadedProjects(sortedThreads);

      // Fetch tools for this user
      const { data: toolsData, error: toolsError } = await supabase
        .from("user_tool_access")
        .select(
          `
          access_level,
          tools (
            id,
            name,
            client,
            purpose,
            features,
            technical_summary,
            link,
            thumbnail,
            additional_detail,
            display_order
          )
        `
        )
        .eq("user_id", currentUser.id)
        .neq("access_level", "hidden")
        .order("tools(display_order)");

      if (toolsError) throw toolsError;

      // Transform tools data
      const transformedTools = toolsData
        .filter((item) => item.tools)
        .map((item) => {
          const tool = item.tools as any;
          return {
            id: tool.id,
            name: tool.name,
            client: tool.client ?? undefined,
            purpose: tool.purpose ?? undefined,
            features: tool.features ?? [],
            technical_summary: tool.technical_summary ?? undefined,
            link: tool.link ?? undefined,
            thumbnail: tool.thumbnail ?? undefined,
            additional_detail: tool.additional_detail ?? undefined,
            access_level: item.access_level,
          };
        }) as Tool[];

      setTools(transformedTools);
    } catch (err) {
      console.error("Error fetching dashboard data:", err);
      setError("Failed to load dashboard");
    } finally {
      setLoading(false);
    }
  };

  const handleRequestAccess = () => {
    window.location.href =
      "mailto:alex@tcglventures.com?subject=Project Access Request&body=I would like to request access to additional projects.";
  };

  const handleToolClick = (tool: Tool) => {
    setSelectedTool(tool);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedTool(null);
  };

  const totalProjects = threadedProjects.reduce(
    (sum, thread) => sum + thread.projects.length,
    0
  );

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-black">
        {user && <Navbar user={user} />}
        <DashboardContainer>
          <PageHeader
            title="Your Portfolio"
            description="Loading your investments..."
          />
          <ProjectGrid>
            <ProjectCardSkeleton />
            <ProjectCardSkeleton />
            <ProjectCardSkeleton />
          </ProjectGrid>
        </DashboardContainer>
      </div>
    );
  }

  // Error state
  if (error || !user) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-white mb-4">Error</h1>
          <p className="text-gray-400">{error || "Failed to load user data"}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black">
      <Navbar user={user} />

      <DashboardContainer>
        {/* Projects Section */}
        <PageHeader
          title="Your Portfolio"
          description="Monitor your investments and upcoming opportunities"
        />

        {/* Empty State */}
        {totalProjects === 0 && (
          <EmptyState
            title="No Projects Yet"
            description="Projects will appear here once you've been granted access. Contact us to request access to investment opportunities."
            action={
              <Button variant="secondary" onClick={handleRequestAccess}>
                Request Access
              </Button>
            }
          />
        )}

        {/* Threaded Projects - Each thread gets its own horizontal row */}
        {threadedProjects.map((threadData) => (
          <div key={threadData.thread.id} className="mb-12">
            {/* Thread Header */}
            <div className="mb-4">
              <h2 className="text-2xl font-black text-white uppercase tracking-wide">
                {threadData.thread.name}
              </h2>
              {threadData.thread.description && (
                <p className="text-sm text-gray-400 mt-1">
                  {threadData.thread.description}
                </p>
              )}
            </div>

            {/* Thread Projects Grid */}
            <ProjectGrid>
              {/* Conditionally render Contents Cards */}
              {threadData.thread.id ===
                "cd3ab3bd-e7b6-4661-85e6-7d2d6fce9462" && (
                <FamilyClubStrategyContentsCard />
              )}
              {threadData.thread.id ===
                "227a1fe9-ba0f-402a-b514-79a057bc2aca" && (
                <HealthTechStrategyContentsCard />
              )}
              {threadData.thread.id ===
                "3f24caa4-57df-473b-985e-0a244ce230c9" && (
                <AssetLiteStrategyContentsCard />
              )}
              {threadData.projects.map((project) => (
                <ProjectCard
                  key={project.id}
                  project={{
                    ...project,
                    user_type: "investor",
                    resource_count: project.resource_count,
                  }}
                  userType="investor"
                  currentUserType={accessLevel ?? undefined}
                />
              ))}
            </ProjectGrid>
          </div>
        ))}

        {/* Tools Section */}
        {tools.length > 0 && (
          <div className="mt-16">
            <PageHeader
              title="Showcase Tools"
              description="Explore case studies of products we've built"
            />
            <ToolGrid>
              {tools.map((tool) => (
                <ToolCard
                  key={tool.id}
                  tool={tool}
                  onClick={() => handleToolClick(tool)}
                />
              ))}
            </ToolGrid>
          </div>
        )}
      </DashboardContainer>

      {/* Tool Modal */}
      <ToolModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        tool={selectedTool}
      />
      {/* Footer Banner */}
      <DashboardFooterBanner />
    </div>
  );
}
